import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";

// Custom legend component to control the order
const CustomLegend = ({ items }) => (
  <div className="flex justify-center gap-6 pt-2 pb-3">
    {items.map((item, index) => (
      <div key={index} className="flex items-center gap-2">
        <div 
          className="w-4 h-0.5" 
          style={{ backgroundColor: item.color }}
        />
        <span className="text-sm text-gray-600">{item.label}</span>
      </div>
    ))}
  </div>
);

const StatsView = ({ stats, categories, weekStartsMonday = true }) => {
  // Calculate category percentages
  const totalCategoryEntries = Object.values(stats.category_counts).reduce((a, b) => a + b, 0);
  const categoryData = Object.entries(categories).map(([key, cat]) => ({
    key,
    name: cat.name,
    color: cat.color,
    count: stats.category_counts[key] || 0,
    percentage: totalCategoryEntries > 0 
      ? Math.round((stats.category_counts[key] || 0) / totalCategoryEntries * 100)
      : 0
  }));

  // Calculate weekly totals
  const thisWeekTotal = stats.weekly_comparison?.reduce((sum, d) => sum + d.thisWeek, 0) || 0;
  const lastWeekTotal = stats.weekly_comparison?.reduce((sum, d) => sum + d.lastWeek, 0) || 0;
  const weeklyDiff = thisWeekTotal - lastWeekTotal;

  // Calculate monthly totals
  const thisMonthTotal = stats.monthly_comparison?.data?.reduce((sum, d) => sum + d.thisMonth, 0) || 0;
  const lastMonthTotal = stats.monthly_comparison?.data?.reduce((sum, d) => sum + d.lastMonth, 0) || 0;
  const monthlyDiff = thisMonthTotal - lastMonthTotal;

  // Transform weekly data to cumulative (line only goes up, never down)
  const weeklyCumulativeData = (stats.weekly_comparison || []).map((item, index, arr) => {
    let thisWeekCumulative = 0;
    let lastWeekCumulative = 0;
    for (let i = 0; i <= index; i++) {
      thisWeekCumulative += arr[i].thisWeek;
      lastWeekCumulative += arr[i].lastWeek;
    }
    return {
      day: item.day,
      thisWeek: thisWeekCumulative,
      lastWeek: lastWeekCumulative
    };
  });

  // Transform monthly data to cumulative (line only goes up, never down)
  const monthlyCumulativeData = (stats.monthly_comparison?.data || []).map((item, index, arr) => {
    let thisMonthCumulative = 0;
    let lastMonthCumulative = 0;
    for (let i = 0; i <= index; i++) {
      thisMonthCumulative += arr[i].thisMonth;
      lastMonthCumulative += arr[i].lastMonth;
    }
    return {
      day: item.day,
      thisMonth: thisMonthCumulative,
      lastMonth: lastMonthCumulative
    };
  });

  return (
    <div className="space-y-6" data-testid="stats-view">
      {/* Consistency Score Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl font-heading">Consistency Score</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                +1-2 pts/day for entries, -4 pts for missed days
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-600">{stats.consistency_score || 0}</p>
              <p className="text-sm text-muted-foreground">points</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={stats.consistency_history || []} 
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="consistencyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date"
                  tick={{ fill: '#6B7280', fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  interval="preserveStartEnd"
                  minTickGap={30}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                  formatter={(value) => [`${value} pts`, 'Score']}
                />
                <Area 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#059669" 
                  strokeWidth={2}
                  fill="url(#consistencyGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#059669' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl font-heading">Weekly Proud Moments</CardTitle>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-600">{thisWeekTotal}</p>
              <p className={`text-sm ${weeklyDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {weeklyDiff >= 0 ? '+' : ''}{weeklyDiff} vs last week
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={weeklyCumulativeData} 
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="day" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  itemSorter={(item) => item.dataKey === 'thisWeek' ? 0 : 1}
                />
                <Line 
                  type="monotone" 
                  dataKey="thisWeek" 
                  name="This Week"
                  stroke="#059669" 
                  strokeWidth={2}
                  dot={{ fill: '#059669', r: 3 }}
                  activeDot={{ r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="lastWeek" 
                  name="Last Week"
                  stroke="#9CA3AF" 
                  strokeWidth={1}
                  dot={{ fill: '#9CA3AF', r: 2 }}
                  activeDot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <CustomLegend items={[
            { label: 'This Week', color: '#059669' },
            { label: 'Last Week', color: '#9CA3AF' }
          ]} />
        </CardContent>
      </Card>

      {/* Monthly Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl font-heading">Monthly Proud Moments</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {stats.monthly_comparison?.thisMonthName || 'This Month'} vs {stats.monthly_comparison?.lastMonthName || 'Last Month'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-600">{thisMonthTotal}</p>
              <p className={`text-sm ${monthlyDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {monthlyDiff >= 0 ? '+' : ''}{monthlyDiff} vs {stats.monthly_comparison?.lastMonthName || 'last month'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={monthlyCumulativeData} 
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="day"
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  ticks={[1, 6, 11, 16, 21, 26, 31]}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelFormatter={(value) => `Day ${value}`}
                  itemSorter={(item) => item.dataKey === 'thisMonth' ? 0 : 1}
                />
                <Line 
                  type="monotone" 
                  dataKey="thisMonth" 
                  name={stats.monthly_comparison?.thisMonthName || 'This Month'}
                  stroke="#059669" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="lastMonth" 
                  name={stats.monthly_comparison?.lastMonthName || 'Last Month'}
                  stroke="#9CA3AF" 
                  strokeWidth={1}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <CustomLegend items={[
            { label: stats.monthly_comparison?.thisMonthName || 'This Month', color: '#059669' },
            { label: stats.monthly_comparison?.lastMonthName || 'Last Month', color: '#9CA3AF' }
          ]} />
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {categoryData.map(cat => (
              <div key={cat.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="category-dot"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="text-sm font-medium">{cat.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {cat.count} ({cat.percentage}%)
                  </span>
                </div>
                <div className="category-progress">
                  <div 
                    className="category-progress-fill"
                    style={{ 
                      width: `${cat.percentage}%`,
                      backgroundColor: cat.color 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatsView;
